import dayjs from "dayjs";
import { DateRange, DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import ReactSlider from "react-slider";

import { useSessionUser } from "@web-app/api";
import { AppFilterType } from "@web-app/types";
import { TIMEZONE_MAP } from "@web-app/utils/helpers";
import { dayjs as dayjsWithTimezone } from "@web-app/utils/time";

import { TabPanelWrapper } from "./TabPanelWrapper";
import { TIME_OPTIONS } from "./data";

export default function DateTimeFilterTab({
  isDateRange,
  onChange,
  setTemporaryAppFilter,
  temporaryAppFilter,
}: {
  isDateRange?: boolean;
  onChange: (appFilter: AppFilterType) => void;
  setTemporaryAppFilter: (appFilter: AppFilterType) => void;
  temporaryAppFilter: AppFilterType;
}) {
  const { data: sessionUser } = useSessionUser();
  const userTimezone = sessionUser?.timezone || "system";

  const convertUTCToUserTimezone = (utcDateString: string): Date => {
    if (userTimezone === "system") {
      return new Date(utcDateString); // Use browser's timezone
    }

    const timezoneToUse = TIMEZONE_MAP[userTimezone] || userTimezone;

    // Parse UTC date and convert to user's timezone
    const utcDate = dayjsWithTimezone.utc(utcDateString);
    const userTzDate = utcDate.tz(timezoneToUse);

    // Extract the date components as they appear in user's timezone
    const year = userTzDate.year();
    const month = userTzDate.month();
    const day = userTzDate.date();
    const hour = userTzDate.hour();
    const minute = userTzDate.minute();
    const second = userTzDate.second();

    // Create a Date object that DayPicker will display correctly
    // DayPicker uses browser's local timezone, so we create date with those components
    return new Date(year, month, day, hour, minute, second);
  };

  // Convert user's timezone selection to UTC for storage
  const convertUserTimezoneToUTC = (date: Date, isEndOfDay = false): Date => {
    if (userTimezone === "system") {
      return isEndOfDay
        ? dayjs(date).endOf("day").toDate()
        : dayjs(date).startOf("day").toDate();
    }

    const timezoneToUse = TIMEZONE_MAP[userTimezone] || userTimezone;

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const userTzDate = dayjsWithTimezone
      .tz(
        `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
        timezoneToUse,
      )
      .startOf("day");

    if (isEndOfDay) {
      return userTzDate.endOf("day").toDate();
    } else {
      return userTzDate.toDate();
    }
  };

  return (
    <TabPanelWrapper>
      <div className="mx-2">
        {isDateRange && (
          <DayPicker
            mode="range"
            selected={{
              from: convertUTCToUserTimezone(
                temporaryAppFilter.scheduledDateTimeStart,
              ),
              to: convertUTCToUserTimezone(
                temporaryAppFilter.scheduledDateTimeEnd,
              ),
            }}
            onSelect={(range: DateRange | undefined) => {
              if (range) {
                const updates: Partial<AppFilterType> = {};

                if (range.from) {
                  const startDate = convertUserTimezoneToUTC(range.from, false);
                  updates.scheduledDateTimeStart = startDate.toISOString();
                }

                if (range.to) {
                  const endDate = convertUserTimezoneToUTC(range.to, true);
                  updates.scheduledDateTimeEnd = endDate.toISOString();
                } else if (range.from && !range.to) {
                  const endDate = convertUserTimezoneToUTC(range.from, true);
                  updates.scheduledDateTimeEnd = endDate.toISOString();
                }

                setTemporaryAppFilter({
                  ...temporaryAppFilter,
                  ...updates,
                });
              }
            }}
          />
        )}
        {!isDateRange && (
          <DayPicker
            mode="single"
            selected={convertUTCToUserTimezone(
              temporaryAppFilter.scheduledDateTimeStart,
            )}
            onSelect={(date: Date | undefined) => {
              if (date) {
                const startDate = convertUserTimezoneToUTC(date, false);
                const endDate = convertUserTimezoneToUTC(date, true);

                onChange({
                  ...temporaryAppFilter,
                  scheduledDateTimeEnd: endDate.toISOString(),
                  scheduledDateTimeStart: startDate.toISOString(),
                });
              }
            }}
          />
        )}
        {!isDateRange && (
          <div className="absolute bottom-10 left-1/2 flex w-[85%] -translate-x-1/2 items-center">
            <ReactSlider
              className="flex w-full items-center"
              thumbClassName="bg-gray-700 p-2 rounded-full text-xs font-semibold"
              trackClassName="bg-gray-200 h-1"
              ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
              renderThumb={(props, state) => {
                return (
                  <div {...props}>
                    <span className="absolute bottom-[20px] left-[-10px] w-10 text-gray-700">
                      {TIME_OPTIONS[state.valueNow].text}
                    </span>
                  </div>
                );
              }}
              marks={Array.from(Array(Object.keys(TIME_OPTIONS).length).keys())}
              pearling
              min={0}
              value={[
                (() => {
                  if (userTimezone === "system") {
                    return dayjs(
                      temporaryAppFilter.scheduledDateTimeStart,
                    ).hour();
                  }
                  const timezoneToUse =
                    TIMEZONE_MAP[userTimezone] || userTimezone;
                  return dayjsWithTimezone(
                    temporaryAppFilter.scheduledDateTimeStart,
                  )
                    .tz(timezoneToUse)
                    .hour();
                })(),
                (() => {
                  if (userTimezone === "system") {
                    return dayjs(
                      temporaryAppFilter.scheduledDateTimeEnd,
                    ).hour();
                  }
                  const timezoneToUse =
                    TIMEZONE_MAP[userTimezone] || userTimezone;
                  return dayjsWithTimezone(
                    temporaryAppFilter.scheduledDateTimeEnd,
                  )
                    .tz(timezoneToUse)
                    .hour();
                })(),
              ]}
              max={Object.keys(TIME_OPTIONS).length - 1}
              markClassName="bg-gray-700"
              onChange={(value) => {
                const utcDate = dayjsWithTimezone.utc(
                  temporaryAppFilter.scheduledDateTimeStart,
                );
                const userTzDate =
                  userTimezone === "system"
                    ? utcDate
                    : utcDate.tz(TIMEZONE_MAP[userTimezone] || userTimezone);

                const year = userTzDate.year();
                const month = userTzDate.month() + 1;
                const day = userTzDate.date();

                const createDateWithTime = (
                  timeOption: (typeof TIME_OPTIONS)[0],
                ) => {
                  if (userTimezone === "system") {
                    return dayjs()
                      .year(year)
                      .month(month - 1)
                      .date(day)
                      .hour(timeOption.h)
                      .minute(timeOption.m)
                      .second(timeOption.s)
                      .millisecond(timeOption.ms)
                      .toDate();
                  }

                  const timezoneToUse =
                    TIMEZONE_MAP[userTimezone] || userTimezone;

                  // Create date in user's timezone with specific time
                  const dateString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

                  return dayjsWithTimezone
                    .tz(dateString, timezoneToUse)
                    .hour(timeOption.h)
                    .minute(timeOption.m)
                    .second(timeOption.s)
                    .millisecond(timeOption.ms)
                    .toDate();
                };

                const startDate = createDateWithTime(TIME_OPTIONS[value[0]]);
                const endDate = createDateWithTime(TIME_OPTIONS[value[1]]);

                onChange({
                  ...temporaryAppFilter,
                  scheduledDateTimeEnd: endDate.toISOString(),
                  scheduledDateTimeStart: startDate.toISOString(),
                });
              }}
            />
          </div>
        )}
      </div>
    </TabPanelWrapper>
  );
}
